<template>
    <div class="space-y-4">
        <div class="flex items-center justify-between gap-4">
            <div>
                <div class="font-semibold text-base">
                    {{ addOn.label }}
                    <span v-if="isPayAtLocation">*</span>
                </div>

                <span class="text-sm text-gray-600">{{ $n(addOn.price, 'currency') }} / {{ addOn.priceType?.label }}</span>
                <AddOnChildren :children="addOn.children" />
            </div>

            <div class="flex items-center gap-3">
                <Qty v-model="qty" :max="maxQty" />
            </div>
        </div>

        <div v-if="hasRequirements && qty > 0" class="rounded-xl border border-slate-200 p-4">
            <div class="flex items-center justify-between gap-4">
                <div class="text-sm text-slate-700 space-y-1">
                    <p v-if="requirementsValuesNotice" class="text-xs text-slate-500">
                        {{ requirementsValuesNotice }}
                    </p>
                </div>

                <Button size="sm" severity="secondary" @click="openRequirementsModal(qty)">
                    {{ $t('Edit details') }}
                </Button>
            </div>
        </div>

        <AddOnRequirementsModal ref="requirementsModalRef" :add-on-label="displayLabel" :requirement-fields="requirementFields" :draft-requirement-items="draftRequirementItems" :general-requirement-error="generalRequirementError" :is-saving-requirements="isSavingRequirements" :get-field-error="getFieldError" :on-field-change="updateDraftRequirementField" :on-close="closeRequirementsModal" :on-save="saveRequirements" :on-add-another="addAnotherRequirementItem" :on-remove-item="removeRequirementItem" :can-add-another="canAddAnotherRequirementItem" />
    </div>
</template>

<script setup>
import AddOnRequirementsModal from './AddOnRequirementsModal.vue'
import AddOnChildren from './AddOnChildren.vue'

const props = defineProps({
    addOn: { type: Object, required: true },

    value: {
        type: [Number, String, Object, null],
        default: 0
    },
    onChange: {
        type: Function,
        required: false
    }
})

const requirements = computed(() => props.addOn?.requirements ?? {})
const hasRequirements = computed(() => {
    return Boolean(requirements.value?.enabled && requirements.value?.inputKey && Array.isArray(requirements.value?.fields) && requirements.value.fields.length > 0)
})
const requirementKey = computed(() => requirements.value?.inputKey ?? null)
const requirementFields = computed(() => (Array.isArray(requirements.value?.fields) ? requirements.value.fields : []))
const isPayAtLocation = computed(() => props.addOn?.payAtLocation || props.addOn?.pay_at_location)
const displayLabel = computed(() => `${props.addOn?.label ?? ''}${isPayAtLocation.value ? ' *' : ''}`)
const requirementsNotice = computed(() => {
    const labels = requirementFields.value.map((field) => field?.label || field?.labelKey || field?.name).filter(Boolean)

    if (labels.length === 0) {
        return props.addOn?.label ?? ''
    }

    return `${props.addOn?.label ?? ''}: ${labels.join(', ')}`
})
const requirementsValuesNotice = computed(() => {
    const hasMultipleItems = requirementItems.value.length > 1

    return requirementItems.value
        .flatMap((item, itemIndex) => {
            return requirementFields.value
                .map((field) => {
                    const fieldLabel = field?.label || field?.labelKey || field?.name
                    const fieldValue = item?.[field?.name]
                    const fieldSuffix = field?.suffix

                    if (!fieldLabel || fieldValue === null || fieldValue === undefined || String(fieldValue).trim() === '') {
                        return null
                    }

                    const itemSuffix = hasMultipleItems ? ` ${itemIndex + 1}` : ''
                    const normalizedValue = String(fieldValue).trim()
                    const renderedValue = fieldSuffix && !normalizedValue.endsWith(String(fieldSuffix)) ? `${normalizedValue} ${fieldSuffix}` : normalizedValue

                    return `${fieldLabel}${itemSuffix}: ${renderedValue}`
                })
                .filter(Boolean)
        })
        .join(', ')
})
const maxQty = computed(() => Number(props.addOn?.maxQuantity ?? 9999))
const requirementsModalRef = ref(null)
const draftRequirementItems = ref([])
const draftQty = ref(0)
const isSavingRequirements = ref(false)
const requirementErrors = ref({})
const generalRequirementError = ref('')
const canAddAnotherRequirementItem = computed(() => draftQty.value < maxQty.value)

const normalizeRequirementItem = (item = {}) => {
    const normalized = {}

    for (const field of requirementFields.value) {
        const incoming = item?.[field.name]

        if (field.type === 'number') {
            normalized[field.name] = incoming === null || incoming === undefined ? '' : String(incoming)
            continue
        }

        normalized[field.name] = incoming ?? ''
    }

    return normalized
}

const normalizedValue = computed(() => {
    if (props.value != null && typeof props.value === 'object') {
        return props.value
    }

    return {
        value: Number(props.value ?? 0)
    }
})

const requirementItems = ref([])

const alignRequirementItemsToQty = (targetQty, seedItems = requirementItems.value) => {
    if (!hasRequirements.value || !requirementKey.value) {
        requirementItems.value = []
        return
    }

    const aligned = []
    for (let index = 0; index < targetQty; index += 1) {
        aligned.push(normalizeRequirementItem(seedItems?.[index]))
    }

    requirementItems.value = aligned
}

watch(
    [() => normalizedValue.value, () => hasRequirements.value, () => requirementKey.value, () => requirementFields.value],
    ([incomingValue]) => {
        if (!hasRequirements.value || !requirementKey.value) {
            requirementItems.value = []
            return
        }

        const qty = Number(incomingValue?.value ?? 0)
        const incomingItems = Array.isArray(incomingValue?.[requirementKey.value]) ? incomingValue[requirementKey.value] : []
        alignRequirementItemsToQty(qty, incomingItems)
    },
    { immediate: true, deep: true }
)

const buildRequirementPayload = () => {
    return requirementItems.value.map((item) => {
        const payloadItem = {}

        for (const field of requirementFields.value) {
            if (field.type === 'number') {
                const parsed = Number(item[field.name])
                payloadItem[field.name] = Number.isFinite(parsed) ? parsed : null
                continue
            }

            payloadItem[field.name] = item[field.name] ?? ''
        }

        return payloadItem
    })
}

const buildPayloadForQty = (nextQty, items = requirementItems.value) => {
    if (!hasRequirements.value || !requirementKey.value) {
        return nextQty
    }

    requirementItems.value = items

    return {
        value: nextQty,
        [requirementKey.value]: buildRequirementPayload()
    }
}

const emitQuantityChange = async (nextQty, items = requirementItems.value) => {
    if (!props.onChange) return

    const payload = buildPayloadForQty(nextQty, items)
    return props.onChange(payload)
}

const clearRequirementErrors = () => {
    requirementErrors.value = {}
    generalRequirementError.value = ''
}

const setRequirementErrors = (error) => {
    const responseErrors = error?.response?.data?.errors
    if (!responseErrors || typeof responseErrors !== 'object') {
        generalRequirementError.value = error?.response?.data?.message ?? 'Request failed.'
        return
    }

    requirementErrors.value = responseErrors
    const collectionError = Array.isArray(responseErrors[requirementKey.value]) ? responseErrors[requirementKey.value][0] : ''
    generalRequirementError.value = collectionError || error?.response?.data?.message || ''
}

const fieldErrorKey = (index, fieldName) => `${requirementKey.value}.${index}.${fieldName}`

const getFieldError = (index, fieldName) => {
    const key = fieldErrorKey(index, fieldName)
    const errors = requirementErrors.value?.[key]

    return Array.isArray(errors) && errors.length > 0 ? errors[0] : ''
}

const openRequirementsModal = async (nextQty) => {
    if (!hasRequirements.value || !requirementKey.value) return

    draftQty.value = nextQty
    clearRequirementErrors()
    draftRequirementItems.value = []
    for (let index = 0; index < nextQty; index += 1) {
        draftRequirementItems.value.push(normalizeRequirementItem(requirementItems.value[index]))
    }

    await nextTick()
    requirementsModalRef.value?.openModal()
}

const closeRequirementsModal = () => {
    clearRequirementErrors()
    requirementsModalRef.value?.closeModal()
}

const updateDraftRequirementField = (index, fieldName, value) => {
    draftRequirementItems.value[index] = {
        ...draftRequirementItems.value[index],
        [fieldName]: value
    }

    const key = fieldErrorKey(index, fieldName)
    if (requirementErrors.value[key]) {
        requirementErrors.value = {
            ...requirementErrors.value,
            [key]: undefined
        }
    }
}

const addAnotherRequirementItem = () => {
    if (!canAddAnotherRequirementItem.value) {
        return
    }

    const nextIndex = draftRequirementItems.value.length
    draftRequirementItems.value.push(normalizeRequirementItem(requirementItems.value[nextIndex]))
    draftQty.value += 1
}

const removeRequirementItem = (index) => {
    if (index < 0 || index >= draftRequirementItems.value.length) {
        return
    }

    draftRequirementItems.value.splice(index, 1)
    draftQty.value = Math.max(0, draftQty.value - 1)
}

const handleQtyChange = async (nextQty) => {
    const currentQty = Number(normalizedValue.value?.value ?? 0)

    if (!hasRequirements.value || !requirementKey.value) {
        await emitQuantityChange(nextQty)
        return
    }

    if (nextQty > currentQty) {
        alignRequirementItemsToQty(nextQty)
        await openRequirementsModal(nextQty)
        return
    }

    alignRequirementItemsToQty(nextQty)
    await emitQuantityChange(nextQty)
}

const saveRequirements = async () => {
    if (!hasRequirements.value || !requirementKey.value) return

    isSavingRequirements.value = true
    clearRequirementErrors()

    try {
        const items = draftRequirementItems.value.map((item) => normalizeRequirementItem(item))
        requirementItems.value = items
        await emitQuantityChange(draftQty.value, items)
        closeRequirementsModal()
    } catch (error) {
        setRequirementErrors(error)
    } finally {
        isSavingRequirements.value = false
    }
}

const qty = computed({
    get() {
        return Number(normalizedValue.value?.value ?? 0)
    },
    set(nextValue) {
        if (!props.onChange) return

        const nextQty = Number(nextValue)
        if (Number.isNaN(nextQty)) return

        void handleQtyChange(nextQty)
    }
})
</script>
